import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBuilding, IconChevronDown, IconPlus, IconSettings, IconLogout, IconCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

export default function OfficeSpaceSwitcher() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { officeSpaces, selectedOfficeSpace, selectOfficeSpace, createOfficeSpace, leaveOfficeSpace, isLoading } = useOfficeSpaceStore();

  const [createOpen, setCreateOpen]         = useState(false);
  const [newOfficeName, setNewOfficeName]   = useState('');
  const [creating, setCreating]             = useState(false);
  const [leaveOpen, setLeaveOpen]           = useState(false);
  const [officeToLeave, setOfficeToLeave]   = useState(null);

  const handleSelect = (os) => {
    selectOfficeSpace(os);
    notifications.show({ title: t('officeSpace.officeSpaceSwitched'), message: t('officeSpace.switchedTo', { name: os.name }), color: 'blue' });
  };

  const handleCreate = async () => {
    if (!newOfficeName.trim()) {
      notifications.show({ title: t('common.error'), message: t('officeSpace.pleaseEnterOfficeSpaceName'), color: 'red' });
      return;
    }
    setCreating(true);
    try {
      await createOfficeSpace({ name: newOfficeName.trim() });
      notifications.show({ title: t('common.success'), message: t('officeSpace.officeSpaceCreated'), color: 'green' });
      setCreateOpen(false);
      setNewOfficeName('');
    } catch (err) {
      notifications.show({ title: t('common.error'), message: err.message || t('officeSpace.failedToCreateOfficeSpace'), color: 'red' });
    } finally {
      setCreating(false);
    }
  };

  const handleLeave = async () => {
    if (!officeToLeave) return;
    try {
      await leaveOfficeSpace(officeToLeave.id);
      notifications.show({ title: t('common.success'), message: t('officeSpace.leftOfficeSpace', { name: officeToLeave.name }), color: 'green' });
      setLeaveOpen(false);
      setOfficeToLeave(null);
    } catch (err) {
      notifications.show({ title: t('common.error'), message: err.message || t('officeSpace.failedToLeaveOfficeSpace'), color: 'red' });
    }
  };

  if (!user || (user.role !== 'OWNER' && user.role !== 'OPERATOR')) return null;

  if (user.role === 'OPERATOR' && officeSpaces.length === 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
        {t('officeSpace.noOfficeSpaceAssigned')}
      </span>
    );
  }

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-2 px-2 py-1.5 rounded-md text-sm min-w-0 max-w-[180px]
                hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors text-zinc-800 dark:text-zinc-100">
                <span className="inline-flex flex-none items-center justify-center w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30">
                  <IconBuilding size={14} className="text-orange-600 dark:text-orange-400" />
                </span>
                {/* Name + chevron collapse below 1190px, leaving just the icon. */}
                <span className="min-w-0 max-w-[110px] truncate font-medium max-[1190px]:hidden">
                  {selectedOfficeSpace?.name || t('officeSpace.selectOfficeSpace')}
                </span>
                <span className="flex-none max-[1190px]:hidden">
                  {isLoading
                    ? <span className="block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : <IconChevronDown size={14} className="opacity-60" />
                  }
                </span>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{selectedOfficeSpace?.name || t('officeSpace.selectOfficeSpace')}</TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end" className="w-72">
          <div className="flex items-center justify-between px-2 py-1.5">
            <DropdownMenuLabel className="p-0 text-xs text-zinc-500">{t('officeSpace.yourOfficeSpaces')}</DropdownMenuLabel>
            {user.role === 'OWNER' && (
              <button
                onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }}
                title={t('officeSpace.createNewOfficeSpace')}
                className="p-1 rounded text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <IconPlus size={14} />
              </button>
            )}
          </div>
          <DropdownMenuSeparator />
          {officeSpaces.map((os) => (
            <DropdownMenuItem key={os.id} onClick={() => handleSelect(os)} className="flex items-center justify-between">
              <span className="flex items-center gap-2 min-w-0 flex-1">
                <IconBuilding size={15} className="text-zinc-500 shrink-0" />
                <span className="font-medium truncate min-w-0" title={os.name}>{os.name}</span>
                {os.isDefault && user.role !== 'OPERATOR' && (
                  <span className="flex-none text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    {t('officeSpace.default')}
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                {selectedOfficeSpace?.id === os.id && <IconCheck size={15} className="text-green-600" />}
                {user.role === 'OWNER' && (
                  <button
                    onClick={() => navigate(`/officespaces/${os.id}/settings`)}
                    title={t('officeSpace.workspaceSettings')}
                    className="p-0.5 rounded text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                  >
                    <IconSettings size={14} />
                  </button>
                )}
                {user.role === 'OPERATOR' && (
                  <button
                    onClick={() => { setOfficeToLeave(os); setLeaveOpen(true); }}
                    title={t('officeSpace.leaveOfficeSpace')}
                    className="p-0.5 rounded text-red-400 hover:text-red-600"
                  >
                    <IconLogout size={13} />
                  </button>
                )}
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/officespaces')} className="justify-center text-[13px] font-semibold text-primary">
            {t('officeSpace.manageAll')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setNewOfficeName(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('officeSpace.createNewOfficeSpace')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                {t('officeSpace.workspaceName')}
              </label>
              <input
                type="text"
                value={newOfficeName}
                onChange={(e) => setNewOfficeName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder={t('officeSpace.enterOfficeSpaceName')}
                className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600
                  bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreateOpen(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                {t('common.cancel')}
              </button>
              <button onClick={handleCreate} disabled={creating}
                className="px-3 py-1.5 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                {creating && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {t('officeSpace.create')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave modal */}
      <Dialog open={leaveOpen} onOpenChange={(v) => { setLeaveOpen(v); if (!v) setOfficeToLeave(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('officeSpace.confirmLeaveOfficeSpace')}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            {t('officeSpace.leaveOfficeSpaceWarning', { name: officeToLeave?.name })}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setLeaveOpen(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
              {t('common.cancel')}
            </button>
            <button onClick={handleLeave} disabled={isLoading}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
              {isLoading && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {t('officeSpace.leave')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
